import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SaveButton } from "@/components/ui/save-button";
import { Spinner } from "@/components/ui/spinner";
import PageTitleBar from "@/components/PageTitleBar";
import { queryClient } from "App";
import IconPicker from "components/icons/IconPicker";
import CustomTableService from "services/CustomTable";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import ACL_RELATIONSHIPS from "acl-relationships";
import { useCustomTablePermissions } from "hooks/useCustomTablePermissions";
import { LABEL_FIELD_TYPES } from "hooks/useRelationshipTargets";
import DeleteColumnModal from "routes/custom_tables/modals/DeleteColumnModal";
import FieldDialog from "routes/custom_tables/modals/FieldDialog";
import { invalidateCustomTablesQuery } from "hooks/useCustomTablesAdminGrid";
import {
  deriveFieldId,
  deriveSingular,
  formatError,
} from "routes/custom_tables/helpers";
import { typeLabel } from "routes/custom_tables/typeLabels";

// Update sends `null` to clear an empty field; create sends `undefined` to omit it.
const shapeTableFields = (data, emptyValue) => ({
  display_name: data.display_name.trim(),
  description: data.description.trim() || undefined,
  icon_name: data.icon_name || emptyValue,
  singular_name: data.singular_name.trim() || emptyValue,
  display_field_id: data.display_field_id || emptyValue,
});

const SAVE_STATUS = {
  IDLE: "idle",
  SAVING: "saving",
  SAVED: "saved",
  ERROR: "error",
};

const AUTOSAVE_MESSAGE = {
  [SAVE_STATUS.IDLE]: "Changes will be saved automatically",
  [SAVE_STATUS.SAVING]: (
    <>
      <Spinner data-icon="inline-start" />
      Autosaving…
    </>
  ),
  [SAVE_STATUS.SAVED]: "Changes saved",
  [SAVE_STATUS.ERROR]: "Save failed — retrying on next change",
};

const PermissionsPanel = ({ slug }) => {
  const { permissions, isFetched, updatePermission, isUpdating } =
    useCustomTablePermissions(slug);

  if (!isFetched) {
    return (
      <div className="tw:p-8 tw:text-center tw:text-muted-foreground">
        Loading permissions…
      </div>
    );
  }

  if (permissions.length === 0) {
    return (
      <div className="tw:p-8 tw:text-center tw:border tw:border-dashed tw:border-border tw:rounded-sm tw:text-muted-foreground tw:bg-accent">
        No roles configured yet. Create a role to grant access to this table.
      </div>
    );
  }

  const applyUpdate = async (roleId, data) => {
    try {
      await updatePermission({ roleId, data });
      toast.success("Permissions updated");
    } catch (err) {
      toast.error(formatError(err, "Failed to update permissions"));
    }
  };

  const handleToggle = (roleId, field, current) =>
    applyUpdate(roleId, { [field]: !current });

  const handleToggleAll = (roleId, next) =>
    applyUpdate(roleId, { can_read: next, can_write: next, can_delete: next });

  const roleName = grant => grant.role_name ?? `Role ${grant.role_id}`;

  const permissionFields = [
    { field: "can_read", label: "Read" },
    { field: "can_write", label: "Write" },
    { field: "can_delete", label: "Delete" },
  ];

  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:mt-3">
      {permissions.map(grant => {
        const flags = permissionFields.map(({ field }) => !!grant[field]);
        const allChecked = flags.every(Boolean);
        const someChecked = flags.some(Boolean);
        const allId = `perm-${grant.role_id}-all`;
        return (
          <div
            key={grant.role_id}
            className="tw:grid tw:grid-cols-[1fr_auto_auto_auto_auto] tw:items-center tw:gap-5 tw:p-3 tw:border tw:border-border tw:rounded-sm tw:bg-[var(--color-surface-raised)] tw:text-foreground"
          >
            <div className="tw:font-semibold">{roleName(grant)}</div>
            <Field orientation="horizontal">
              <Checkbox
                id={allId}
                checked={allChecked}
                indeterminate={someChecked && !allChecked}
                disabled={isUpdating}
                onCheckedChange={() =>
                  handleToggleAll(grant.role_id, !allChecked)
                }
              />
              <FieldLabel htmlFor={allId} className="tw:font-normal">
                All
              </FieldLabel>
            </Field>
            {permissionFields.map(({ field, label }) => {
              const id = `perm-${grant.role_id}-${field}`;
              return (
                <Field key={field} orientation="horizontal">
                  <Checkbox
                    id={id}
                    checked={!!grant[field]}
                    disabled={isUpdating}
                    onCheckedChange={() =>
                      handleToggle(grant.role_id, field, grant[field])
                    }
                  />
                  <FieldLabel htmlFor={id} className="tw:font-normal">
                    {label}
                  </FieldLabel>
                </Field>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const CreatePermissionsNotice = () => (
  <div className="tw:mt-8 tw:pt-4 tw:border-t tw:border-border">
    <h3 className="tw:m-0 tw:mb-1 tw:font-heading tw:text-foreground">
      Permissions
    </h3>
    <p className="tw:text-muted-foreground tw:text-xs tw:leading-snug">
      Access is off by default. Your role is granted full access automatically.
      Configure access for other roles after the table is created.
    </p>
  </div>
);

const SortableColumnRow = ({ column, onEdit, onDelete, locked = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.field_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="tw:flex tw:items-center tw:gap-3 tw:p-3 tw:border tw:border-border tw:rounded-sm tw:bg-[var(--color-surface-raised)] tw:text-foreground"
    >
      <button
        type="button"
        className="tw:bg-transparent tw:border-none tw:text-muted-foreground tw:cursor-grab tw:p-1 tw:hover:text-foreground tw:active:cursor-grabbing"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="tw:size-4" />
      </button>
      <div className="tw:flex tw:items-center tw:gap-2 tw:flex-1 tw:flex-wrap">
        <span className="tw:font-semibold">{column.display_name}</span>
        <Badge variant="secondary" code>
          {column.field_id}
        </Badge>
        <Badge variant="secondary">{typeLabel(column.type)}</Badge>
        {column.is_required && <Badge variant="outline">Required</Badge>}
        {column.is_unique && <Badge variant="outline">Unique</Badge>}
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Edit column"
        onClick={() => onEdit?.(column)}
        disabled={locked}
      >
        <Pencil />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Delete column"
        onClick={() => onDelete?.(column)}
        disabled={locked}
      >
        <Trash2 />
      </Button>
    </div>
  );
};

const SchemaEditor = () => {
  const { slug: routeSlug } = useParams();
  const navigate = useNavigate();
  const isEdit = !!routeSlug;
  const canManageSchema = useAuthorized([
    ACL_RELATIONSHIPS.customTable.editSchema,
  ]);
  const [deleteColumn, setDeleteColumn] = useState(null);
  const [fieldDialog, setFieldDialog] = useState(null);
  const [saveStatus, setSaveStatus] = useState(SAVE_STATUS.IDLE);
  const saveTimer = useRef(null);

  const schemaQueryKey = useMemo(
    () => ["custom-table-schema", routeSlug],
    [routeSlug]
  );
  const invalidateSchema = useCallback(
    () => queryClient.invalidateQueries({ queryKey: schemaQueryKey }),
    [schemaQueryKey]
  );

  const {
    data: schema,
    isFetched,
    isFetching,
  } = useQuery({
    queryKey: schemaQueryKey,
    queryFn: () => CustomTableService.get(routeSlug),
    enabled: isEdit,
  });

  usePageTitle(schema?.display_name || "Custom Table Settings");

  const values = useMemo(
    () => ({
      display_name: schema?.display_name ?? "",
      singular_name: schema?.singular_name ?? "",
      icon_name: schema?.icon_name ?? null,
      description: schema?.description ?? "",
      display_field_id: schema?.display_field_id ?? "",
    }),
    [schema]
  );

  const { control, setValue, getValues, reset, handleSubmit, formState } =
    useForm({
      values,
      resetOptions: { keepDirtyValues: true },
      mode: "onBlur",
    });

  // Create-mode columns are part of the form (one source of truth for the
  // create payload); edit-mode columns come from the server-backed query below.
  const {
    fields: draftColumns,
    append: appendDraft,
    update: updateDraft,
    remove: removeDraft,
    move: moveDraft,
  } = useFieldArray({ control, name: "columns" });

  const displayNameValue = useWatch({ control, name: "display_name" }) || "";
  const singularValue = useWatch({ control, name: "singular_name" }) || "";
  const singularOverridden =
    !!singularValue.trim() &&
    singularValue !== deriveSingular(displayNameValue);

  const columns = useMemo(
    () =>
      isEdit
        ? [...(schema?.columns ?? [])].sort(
            (a, b) => (a.position ?? 0) - (b.position ?? 0)
          )
        : draftColumns,
    [isEdit, schema, draftColumns]
  );

  const runSave = useCallback(
    () =>
      handleSubmit(async data => {
        try {
          await CustomTableService.update(
            routeSlug,
            shapeTableFields(data, null)
          );
          await invalidateCustomTablesQuery();
          reset(data);
          setSaveStatus(SAVE_STATUS.SAVED);
          toast.success("Saved");
        } catch (err) {
          setSaveStatus(SAVE_STATUS.ERROR);
          toast.error(formatError(err, "Failed to save table"));
        }
      })(),
    [handleSubmit, reset, routeSlug]
  );

  // Only edit mode autosaves; create mode persists on "Create table".
  const scheduleSave = useCallback(() => {
    if (!isEdit) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(runSave, 700);
  }, [isEdit, runSave]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  // RHF's field.onChange takes either a DOM event or a raw value, so this one
  // wrapper serves both the Input/select controls and IconPicker.
  const autosaveOnChange = onChange => value => {
    onChange(value);
    scheduleSave();
  };

  const handleDisplayNameChange = (next, field) => {
    const prev = getValues("display_name");
    const currentSingular = getValues("singular_name");
    field.onChange(next);
    // Keep the singular in sync only while the user hasn't overridden it.
    if (!currentSingular?.trim() || currentSingular === deriveSingular(prev)) {
      setValue("singular_name", deriveSingular(next), { shouldDirty: true });
    }
    scheduleSave();
  };

  const handleResetSingular = () => {
    setValue("singular_name", deriveSingular(getValues("display_name")), {
      shouldDirty: true,
    });
    scheduleSave();
  };

  const derivedSlug = isEdit ? routeSlug : deriveFieldId(displayNameValue);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const existingFieldNames = useMemo(
    () => columns.map(c => c.field_id),
    [columns]
  );

  const displayColumnOptions = useMemo(
    () => columns.filter(c => LABEL_FIELD_TYPES.has(c.type)),
    [columns]
  );

  const rowCountForBackfill = schema?.row_count ?? 0;

  // The dialog is presentational: it reports the field payload back here and
  // the parent owns persistence (server in edit mode, the draft field array in
  // create mode) plus the success toast. Errors propagate so the dialog can
  // surface them (including a unique-violation's affected rows).
  const handleFieldSubmit = async payload => {
    const { mode, column } = fieldDialog;
    if (mode === "add") {
      if (isEdit) {
        await CustomTableService.addColumn(routeSlug, payload);
        await invalidateSchema();
        setSaveStatus(SAVE_STATUS.SAVED);
        toast.success("Field added");
      } else {
        appendDraft({ ...payload, position: draftColumns.length + 1 });
      }
    } else if (isEdit) {
      await CustomTableService.updateColumn(
        routeSlug,
        column.field_id,
        payload
      );
      await invalidateSchema();
      setSaveStatus(SAVE_STATUS.SAVED);
      toast.success("Field updated");
    } else {
      const index = draftColumns.findIndex(c => c.field_id === column.field_id);
      if (index >= 0) updateDraft(index, { ...column, ...payload });
    }
  };

  const handleDeleteColumn = useCallback(
    column => {
      if (isEdit) {
        setDeleteColumn({
          slug: routeSlug,
          fieldId: column.field_id,
          displayName: column.display_name,
        });
      } else {
        removeDraft(
          draftColumns.findIndex(c => c.field_id === column.field_id)
        );
      }
    },
    [isEdit, routeSlug, removeDraft, draftColumns]
  );

  const handleEditColumn = useCallback(
    column => setFieldDialog({ mode: "edit", column }),
    []
  );

  const handleDragEnd = async event => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;
    const oldIndex = columns.findIndex(c => c.field_id === active.id);
    const newIndex = columns.findIndex(c => c.field_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    if (!isEdit) {
      moveDraft(oldIndex, newIndex);
      return;
    }

    const reordered = arrayMove(columns, oldIndex, newIndex);

    queryClient.setQueryData(schemaQueryKey, old =>
      old ? { ...old, columns: reordered } : old
    );
    try {
      await CustomTableService.reorderColumns(
        routeSlug,
        reordered.map(c => c.field_id)
      );
      await invalidateSchema();
      setSaveStatus(SAVE_STATUS.SAVED);
      toast.success("Column order saved");
    } catch (err) {
      await invalidateSchema();
      setSaveStatus(SAVE_STATUS.ERROR);
      toast.error(formatError(err, "Failed to save column order"));
    }
  };

  const canSubmit =
    !!displayNameValue.trim() && columns.length > 0 && !formState.isSubmitting;

  // Any in-flight save reads as "saving": the metadata autosave (isSubmitting)
  // and column add/edit/delete/reorder (which refetch the schema, isFetching).
  const shownSaveStatus =
    isFetched && (isFetching || formState.isSubmitting)
      ? SAVE_STATUS.SAVING
      : saveStatus;

  const create = handleSubmit(async data => {
    try {
      const created = await CustomTableService.create({
        ...shapeTableFields(data, undefined),
        columns: (data.columns ?? []).map(
          ({ id: _id, position: _position, ...rest }) => rest
        ),
      });
      await invalidateCustomTablesQuery();
      toast.success("Table created");
      // Fall back to the list so a missing slug doesn't strand the user on a
      // 404 edit page (.../undefined/edit).
      if (created?.slug) {
        navigate(`/settings/custom_tables/${created.slug}/edit`);
      } else {
        navigate("/settings/custom_tables");
      }
    } catch (err) {
      toast.error(formatError(err, "Failed to save table"));
    }
  });

  if (isEdit && !isFetched) {
    return (
      <div className="tw:p-8 tw:text-center tw:text-muted-foreground">
        Loading schema…
      </div>
    );
  }

  const pageTitle = isEdit
    ? displayNameValue.trim() || schema?.display_name || routeSlug
    : displayNameValue.trim() || "New Table";
  const showPermissionsPanel = isEdit && canManageSchema;

  return (
    <>
      <FieldDialog
        open={!!fieldDialog}
        onOpenChange={next => !next && setFieldDialog(null)}
        mode={fieldDialog?.mode}
        column={fieldDialog?.column}
        existingFieldIds={existingFieldNames}
        rowCount={rowCountForBackfill}
        onSubmit={handleFieldSubmit}
      />
      <DeleteColumnModal
        key={deleteColumn?.fieldId}
        open={!!deleteColumn}
        onOpenChange={next => !next && setDeleteColumn(null)}
        slug={deleteColumn?.slug}
        fieldId={deleteColumn?.fieldId}
        displayName={deleteColumn?.displayName}
      />
      <div className="tw:text-foreground tw:font-sans">
        <PageTitleBar
          backTo="/settings/custom_tables"
          backLabel="Back to Custom Tables"
          title={pageTitle}
          className="tw:mb-5"
          actions={
            isEdit ? (
              <span
                className="tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-muted-foreground"
                role="status"
                aria-live="polite"
              >
                {AUTOSAVE_MESSAGE[shownSaveStatus]}
              </span>
            ) : (
              <SaveButton
                disabled={!canSubmit}
                saving={formState.isSubmitting}
                onClick={create}
              >
                Create table
              </SaveButton>
            )
          }
        />

        <div className="tw:bg-card tw:border tw:border-border tw:rounded-md tw:p-6 tw:shadow-xs">
          <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mb-4 tw:max-md:grid-cols-1">
            <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:min-w-0">
              <FieldLabel
                htmlFor="schema-name"
                className="tw:text-muted-foreground tw:text-xs tw:font-semibold"
              >
                Name
              </FieldLabel>
              <Controller
                name="display_name"
                control={control}
                rules={{ validate: v => !!v?.trim() }}
                render={({ field }) => (
                  <Input
                    id="schema-name"
                    value={field.value ?? ""}
                    onBlur={field.onBlur}
                    onChange={e =>
                      handleDisplayNameChange(e.target.value, field)
                    }
                    placeholder="UTM Sources"
                  />
                )}
              />
              <FieldDescription className="tw:text-muted-foreground tw:text-xs tw:leading-snug">
                Plural form. Shown in nav and headers.
              </FieldDescription>
            </Field>
            <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:min-w-0">
              <FieldLabel
                htmlFor="schema-singular"
                className="tw:text-muted-foreground tw:text-xs tw:font-semibold"
              >
                Singular name
              </FieldLabel>
              <Controller
                name="singular_name"
                control={control}
                render={({ field }) => (
                  <Input
                    id="schema-singular"
                    value={field.value ?? ""}
                    onBlur={field.onBlur}
                    onChange={autosaveOnChange(field.onChange)}
                    placeholder="UTM Source"
                  />
                )}
              />
              <FieldDescription className="tw:text-muted-foreground tw:text-xs tw:leading-snug">
                {singularOverridden ? (
                  <>
                    <span className="tw:mr-1">{`Used in "New ${singularValue || "Item"}" and "Edit
                      ${singularValue || "Item"}" titles`}</span>
                    <Button
                      variant="link"
                      className="tw:h-auto tw:p-0 tw:text-xs"
                      onClick={handleResetSingular}
                    >
                      Reset to auto-derived
                    </Button>
                  </>
                ) : (
                  <>
                    Auto-derived from the name. Override for irregular plurals.
                  </>
                )}
              </FieldDescription>
            </Field>
          </div>

          <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mb-4 tw:max-md:grid-cols-1">
            <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:min-w-0">
              <FieldLabel className="tw:text-muted-foreground tw:text-xs tw:font-semibold">
                Icon
              </FieldLabel>
              <Controller
                name="icon_name"
                control={control}
                render={({ field }) => (
                  <IconPicker
                    value={field.value}
                    onChange={autosaveOnChange(field.onChange)}
                  />
                )}
              />
            </Field>
            <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:min-w-0">
              <FieldLabel className="tw:text-muted-foreground tw:text-xs tw:font-semibold">
                Table ID
              </FieldLabel>
              {isEdit ? (
                <Badge
                  variant="secondary"
                  code
                  className="tw:cursor-not-allowed tw:text-muted-foreground"
                >
                  {derivedSlug}
                </Badge>
              ) : (
                <Badge variant="secondary" code>
                  {derivedSlug || "—"}
                </Badge>
              )}
            </Field>
            <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:min-w-0">
              <FieldLabel
                htmlFor="schema-display-column"
                className="tw:text-muted-foreground tw:text-xs tw:font-semibold"
              >
                Display column
              </FieldLabel>
              <Controller
                name="display_field_id"
                control={control}
                render={({ field }) => {
                  const displayItems = [
                    { label: "First text column (default)", value: "" },
                    ...displayColumnOptions.map(c => ({
                      label: c.display_name,
                      value: c.field_id,
                    })),
                  ];
                  return (
                    <Select
                      items={displayItems}
                      value={field.value ?? ""}
                      onValueChange={autosaveOnChange(field.onChange)}
                    >
                      <SelectTrigger id="schema-display-column">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {displayItems.map(item => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  );
                }}
              />
              <FieldDescription className="tw:text-muted-foreground tw:text-xs tw:leading-snug">
                Names each row in delete confirmations, relationship labels, and
                page titles.
              </FieldDescription>
            </Field>
          </div>

          <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:mb-6">
            <FieldLabel
              htmlFor="schema-description"
              className="tw:text-muted-foreground tw:text-xs tw:font-semibold"
            >
              Description
            </FieldLabel>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Input
                  id="schema-description"
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  onChange={autosaveOnChange(field.onChange)}
                  placeholder="Optional description"
                />
              )}
            />
          </Field>

          <div className="tw:flex tw:items-center tw:justify-between tw:mt-4 tw:mb-3 tw:pt-4 tw:border-t tw:border-border">
            <h3 className="tw:m-0 tw:font-heading tw:text-foreground">
              Columns
            </h3>
            <Button
              size="sm"
              aria-label="Open add field dialog"
              onClick={() => setFieldDialog({ mode: "add" })}
            >
              Add Field
            </Button>
          </div>

          {columns.length === 0 ? (
            <div className="tw:p-8 tw:text-center tw:border tw:border-dashed tw:border-border tw:rounded-sm tw:text-muted-foreground tw:bg-accent">
              No columns yet. Click <strong>Add Field</strong> to define the
              first one.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={columns.map(c => c.field_id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="tw:flex tw:flex-col tw:gap-2">
                  {columns.map(column => (
                    <SortableColumnRow
                      key={column.field_id}
                      column={column}
                      onEdit={handleEditColumn}
                      onDelete={handleDeleteColumn}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {showPermissionsPanel && (
            <div className="tw:mt-8 tw:pt-4 tw:border-t tw:border-border">
              <h3 className="tw:m-0 tw:mb-1 tw:font-heading tw:text-foreground">
                Permissions
              </h3>
              <p className="tw:text-muted-foreground tw:text-xs tw:leading-snug">
                Toggle read, write, and delete access per role. Admins always
                have full access.
              </p>
              <PermissionsPanel slug={routeSlug} />
            </div>
          )}

          {!isEdit && canManageSchema && <CreatePermissionsNotice />}
        </div>
      </div>
    </>
  );
};

export default SchemaEditor;
